import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { styled } from '@mui/material/styles';
import { LoaderSkeleton, Switch, SingleLineText, DatePicker, Tag, Modal } from '@bloomays-lib/ui.shared';
import { BusinessType, Billing, PaymentTerms } from '@bloomays-lib/types.shared';
import {
  GridColDef,
  GridRenderCellParams,
  gridClasses,
  GridRowModes,
  GridRowModesModel,
  GridActionsCellItem,
  GridEventListener,
  GridRowEditStopReasons,
  useGridApiRef,
  GridCellParams,
  GridRowId,
  GridRenderEditCellParams,
  GridValueGetter,
  GridRowClassNameParams,
  GridRowParams,
  GridActionsCellItemProps,
} from '@mui/x-data-grid-premium';
import RawDataGrid from '../molecules/DataGrid';
import { format } from 'date-fns/fp/format';
import IconButton from '@mui/material/IconButton';
import values from 'lodash/fp/values';
import { invoiceStatus, renderInvoiceStatusCell } from '../../helpers/invoiceDatagrid';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale/fr';
import React, { useState } from 'react';
import pick from 'lodash/fp/pick';
import ActionInvoice from '../molecules/ActionInvoice';

export type BillingDashboardProps = {
  billings?: Billing[];
  isTrialPeriod: boolean;
  onChange: () => void;
  loading: boolean;
  updateBilling: (billing: Partial<Billing>) => Promise<Partial<Billing>>;
  onError?: (error: any) => void;
  createBillingCustomerInvoice?: (v: { variables: { billingRecordId: string; force: boolean } }) => void;
};

const renderIconLabel = (label: string, emoji?: string, onClick?: () => void) => {
  return (
    <IconButton size="small" onClick={onClick} title={label}>
      {emoji || label}
    </IconButton>
  ) as React.ReactNode;
};

const editableFields = [
  'billingDate',
  'trialPeriodEndDate',
  'description',
  'taxes',
  'bdc',
  'replacement',
  'cancelled',
  'businessType',
];

const isCellEditable = (billing: Billing) => {
  return !billing.billed && !billing.cancelled;
};

const formatPaymentTerms = (terms?: PaymentTerms) => {
  switch (terms) {
    case PaymentTerms.FIFTY_FIFTY_SIGNATURE_START:
      return '50% signature / 50% démarrage';
    case PaymentTerms.FIFTY_FIFTY_START_TRIAL:
      return '50% démarrage / 50% fin PE';
    case PaymentTerms.FULL_START:
      return '100% au démarrage';
    case PaymentTerms.CUSTOM:
      return 'Conditions particulières';
    default:
      return 'Non défini';
  }
};

const paymentTermsOptions = Object.values(PaymentTerms).map((term) => ({
  value: term,
  label: formatPaymentTerms(term),
}));

const businessTypeOptions = Object.values(BusinessType).map((type) => ({
  value: type,
  label: type,
}));

export const BillingDashboard = ({
  billings,
  isTrialPeriod,
  onChange,
  loading,
  updateBilling,
  onError,
  createBillingCustomerInvoice,
}: BillingDashboardProps) => {
  const [openActionCustomerInvoice, setOpenActionCustomerInvoice] = useState<{
    billing?: Billing;
    open: boolean;
  }>({ open: false });
  const pageSize = 50;
  const apiRef = useGridApiRef();
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = async (newRow: Billing, oldRow: Billing) => {
    try {
      const updates = pick(editableFields, newRow);
      const updatedRow = await updateBilling({ ...updates, recordId: oldRow.recordId });
      return { ...newRow, ...updatedRow };
    } catch (error) {
      onError?.(error);
      return oldRow;
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const renderDatePickerCell = (params: GridRenderEditCellParams<Billing>) => {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
          <DatePicker
            value={params.value}
            handleChange={(newValue: Date) => {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: newValue,
              });
            }}
          />
        </LocalizationProvider>
      </div>
    ) as React.ReactNode;
  };

  const columns: GridColDef<Billing>[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      headerClassName: 'header',
      getActions: ({ id, row }: GridRowParams<Billing>) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        const isDisabled = !isCellEditable(row);

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ] as React.ReactElement<GridActionsCellItemProps>[];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
            disabled={isDisabled}
          />,
        ] as React.ReactElement<GridActionsCellItemProps>[];
      },
    },
    {
      field: 'invoiceStatus',
      headerName: 'Statut de la facture',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 150,
      editable: editableFields.includes('invoiceStatus'),
      type: 'singleSelect',
      valueOptions: values(invoiceStatus),
      valueGetter: (value: GridValueGetter<Billing>, row: Billing) => {
        return value || 'Non défini';
      },
      renderCell: renderInvoiceStatusCell<Billing>(
        'invoiceStatus',
        'lastCustomerInvoiceError',
        (row) => {
          return !row.billed;
        },
        (billing: Billing) => () => {
          if (billing.cancelled) {
            return;
          }
          setOpenActionCustomerInvoice({ open: true, billing });
          return undefined;
        },
      ),
    },
    {
      field: 'customerName',
      headerName: 'Client',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 200,
      editable: editableFields.includes('customerName'),
    },
    {
      field: 'description',
      headerName: 'Titre du poste',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 200,
      editable: editableFields.includes('description'),
    },

    {
      field: 'billingContactFullname',
      headerName: 'Contact de facturation',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 200,
      editable: editableFields.includes('billingContactFullname'),
      valueGetter: (value: GridValueGetter<Billing>, row: Billing) => {
        const contacts = row.billingContactFullname;
        if (!contacts || contacts.length === 0) return '';
        return contacts.join(', ');
      },
    },
    {
      field: 'billingDate',
      headerName: 'Début de démarrage / Facturation',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      type: 'date',
      flex: 1,
      minWidth: 150,
      editable: editableFields.includes('billingDate'),
      renderEditCell: renderDatePickerCell,
      valueGetter: (value: GridValueGetter<Billing>, row: Billing) => {
        return row.billingDate ? new Date(convertDateToStringFormat(row.billingDate)) : null;
      },
      valueFormatter: (value: Date) => {
        if (!value) return '';
        return format('dd/MM/yyyy')(value);
      },
      valueSetter: (value: Date, row: Billing) => {
        return {
          ...row,
          billingDate: value ? convertDateToStringFormat(value, 'yyyy-MM-dd') : row.billingDate,
        };
      },
    },
    {
      field: 'trialPeriodEndDate',
      headerName: 'Fin de la PE',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      type: 'date',
      flex: 1,
      minWidth: 150,
      editable: editableFields.includes('trialPeriodEndDate'),
      renderEditCell: renderDatePickerCell,
      valueGetter: (value: GridValueGetter<Billing>, row: Billing) => {
        return row.trialPeriodEndDate ? new Date(convertDateToStringFormat(row.trialPeriodEndDate)) : null;
      },
      valueFormatter: (value: Date) => {
        if (!value) return '';
        return format('dd/MM/yyyy')(value);
      },
      valueSetter: (value: Date, row: Billing) => {
        return {
          ...row,
          trialPeriodEndDate: value ? convertDateToStringFormat(value, 'yyyy-MM-dd') : row.trialPeriodEndDate,
        };
      },
    },
    {
      field: 'isInTrialPeriod',
      headerName: "En période d'essai",
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      type: 'boolean',
      flex: 1,
      minWidth: 150,
      editable: editableFields.includes('isInTrialPeriod'),
      valueGetter: (value: GridValueGetter<Billing>, row: Billing) => {
        if (!row.trialPeriodEndDate) return false;
        const trialEndDate = new Date(convertDateToStringFormat(row.trialPeriodEndDate));
        return trialEndDate > new Date();
      },
    },
    {
      field: 'salesPriceWithoutTaxes',
      headerName: 'Marge Hors Taxes',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 150,
      editable: editableFields.includes('salesPriceWithoutTaxes'),
      type: 'number',
      valueFormatter: (value: number) => {
        return value ? `${value} €` : null;
      },
    },
    {
      field: 'taxes',
      headerName: 'TVA',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 100,
      editable: editableFields.includes('taxes'),
      type: 'number',
      valueFormatter: (value: number) => {
        return value !== null ? `${value} %` : null;
      },
    },

    {
      field: 'externalCustomerInvoiceId',
      headerName: 'N° de facture',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 150,
      editable: editableFields.includes('externalCustomerInvoiceId'),
      renderCell: (params: GridRenderCellParams<Billing>) => {
        if (!params.row.externalCustomerInvoiceId || !params.row.externalCustomerInvoiceUrl) {
          return params.row.externalCustomerInvoiceId || '';
        }
        return (
          <a
            href={params.row.externalCustomerInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {params.row.externalCustomerInvoiceId}
          </a>
        ) as React.ReactNode;
      },
    },
    {
      field: 'cancelled',
      headerName: 'Annulé',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      type: 'boolean',
      flex: 1,
      minWidth: 100,
      editable: editableFields.includes('cancelled'),
      renderCell: (params: GridRenderCellParams<Billing>) => {
        return params.row.cancelled ? renderIconLabel('Annulé', '❌') : 'Non';
      },
      valueGetter: (value: GridValueGetter<Billing>, row: Billing) => {
        return value || false;
      },
    },

    {
      field: 'bdc',
      headerName: 'Bon de commande',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 150,
      editable: editableFields.includes('bdc'),
      valueFormatter: (value: string) => {
        return value || 'Non défini';
      },
    },
    {
      field: 'replacement',
      headerName: 'Remplacement',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      type: 'boolean',
      flex: 1,
      minWidth: 100,
      editable: editableFields.includes('replacement'),
      renderCell: (params: GridRenderCellParams<Billing>) => {
        return params.row.replacement ? renderIconLabel('Remplacement', '🔄') : renderIconLabel('Non', '🤑');
      },
    },
    {
      field: 'businessType',
      headerName: 'Type',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 100,
      editable: editableFields.includes('businessType'),
      type: 'singleSelect',
      valueOptions: businessTypeOptions,
      renderCell(params: GridRenderCellParams<Billing>) {
        return params.value ? ((<Tag text={params.value} />) as React.ReactNode) : null;
      },
    },
    {
      field: 'paymentTerms',
      headerName: 'Mode de facturation',
      headerClassName: 'header',
      sortable: true,
      groupable: true,
      filterable: true,
      flex: 1,
      minWidth: 260,
      editable: editableFields.includes('paymentTerms'),
      type: 'singleSelect',
      valueOptions: paymentTermsOptions,
      valueGetter: (value: GridValueGetter<Billing>, row: Billing) => {
        return row.paymentTerms || PaymentTerms.FULL_START;
      },
      valueFormatter: (value: PaymentTerms) => {
        return formatPaymentTerms(value);
      },
      renderCell(params: GridRenderCellParams<Billing>) {
        return (<Tag text={formatPaymentTerms(params.value)} />) as React.ReactNode;
      },
    },
    {
      field: 'paymentTermsDetails',
      headerName: 'Conditions particulières',
      headerClassName: 'header',
      filterable: true,
      flex: 1,
      minWidth: 260,
      editable: editableFields.includes('paymentTermsDetails'),
    },
  ];

  return (
    <div>
      <Modal open={openActionCustomerInvoice.open} onClose={() => setOpenActionCustomerInvoice({ open: false })}>
        {openActionCustomerInvoice.billing ? (
          <ActionInvoice
            mode="customer"
            invoiceLabel={openActionCustomerInvoice.billing.description || ''}
            activityId={openActionCustomerInvoice.billing.recordId}
            lastError={openActionCustomerInvoice.billing.lastCustomerInvoiceError || ''}
            invoiceStatus={openActionCustomerInvoice.billing.invoiceStatus}
            clickHandler={async (force: boolean) => {
              if (!createBillingCustomerInvoice) {
                return;
              }
              if (!openActionCustomerInvoice?.billing?.recordId) {
                return;
              }
              await createBillingCustomerInvoice({
                variables: {
                  billingRecordId: openActionCustomerInvoice.billing.recordId,
                  force: force,
                },
              });
              setOpenActionCustomerInvoice({ open: false });
            }}
          />
        ) : undefined}
      </Modal>
      <ContainerToggleText>
        <Switch id="isTrialPeriod" checked={isTrialPeriod} onSwitch={onChange} label={''} />
        <SingleLineText text="Afficher uniquement les bloomers en période d'essai" />
      </ContainerToggleText>
      {loading ? (
        <LoaderSkeleton height={800} width={1200} />
      ) : (
        <DataGrid
          apiRef={apiRef}
          uniqueDatagridId="billing-dashboard"
          initialState={{
            sorting: {
              sortModel: [
                { field: 'invoiceStatus', sort: 'desc' },
                { field: 'billingDate', sort: 'asc' },
              ],
            },
          }}
          fetchLoading={loading}
          getRowClassName={(params: GridRowClassNameParams) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          hideFooterPagination={false}
          sortingMode="client"
          filterMode="client"
          rows={billings || []}
          getRowId={(row: Billing) => row.recordId || ''}
          columns={columns}
          pageSize={pageSize}
          pagination={true}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={onError}
          isCellEditable={(params: GridCellParams) => {
            return isCellEditable(params.row);
          }}
          sx={{
            width: '100%',
          }}
        />
      )}
    </div>
  ) as React.ReactNode;
};

export default BillingDashboard;

const DataGrid = styled(RawDataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.neutral[10],
  },
}));

const ContainerToggleText = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
  margin: '1em auto',
}));
